/**
 * <image-comparison> — docs/07 §8, docs/35 B6. Step 9.10.
 *
 * The clip is one custom property, `--percent`, written from the range
 * input's value. Everything the visitor can do — drag, click the track,
 * arrow keys, Home/End, touch — is the platform's, because the control IS an
 * `<input type="range">`. See the note in snippets/image-comparison.liquid.
 *
 * IIFE-wrapped for the same reason as the other section scripts: a page may
 * carry two custom-content sections and the tag would execute twice
 * (docs/54 §5.6).
 */
(function () {
  if (customElements.get('image-comparison')) return;

  const REDUCED = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ImageComparison extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.range = this.querySelector('[data-comparison-range]');
      if (!this.range) return;

      this.set(this.range.value);
      this.range.addEventListener('input', () => this.set(this.range.value));

      if (this.dataset.animate === 'true') this.armSweep();
    }

    /**
     * Move the divider, and retire whichever label no longer has an image
     * under it — docs/54 §1.-35.
     *
     * THE BUG THIS FIXES read as "the before doesn't completely vanish".
     * It did vanish: at 0% the after layer covers the frame and the greyscale
     * image is gone. What stayed was the word BEFORE, sitting in the bottom
     * left over an image that was now entirely the after shot — so the badge
     * asserted "before" about a picture with no before in it, and the section
     * looked broken rather than finished.
     *
     * The AFTER label never showed the same fault only by accident: it lives
     * inside the clipped layer, so it is cut away with its own image. The
     * BEFORE label sits on the base layer, which is never clipped.
     *
     * Rather than move the markup, both are now driven from the same number
     * that drives the clip — so they cannot disagree with it. 6% is roughly
     * the label's own width at the narrowest band; below that there is no
     * room for it to be true.
     */
    set(percent) {
      const value = Number(percent);
      this.style.setProperty('--percent', `${value}%`);
      this.toggleAttribute('data-hide-before', value <= 6);
      this.toggleAttribute('data-hide-after', value >= 94);
    }

    /**
     * docs/07 §8's sweep: the divider enters at 95% and settles to 50% when
     * the section comes into view, which is what tells the visitor the
     * handle moves at all.
     *
     * THE REFERENCE DOES NOT GUARD THIS. docs/07 §8 records it as "the only
     * scroll-linked animation on the site, and not reduced-motion guarded",
     * and docs/08 §9 adds that it runs on mobile where every other reveal is
     * stripped. Guarded here in JS rather than only in CSS: under reduced
     * motion the observer is never created and the divider simply starts
     * where it belongs.
     */
    armSweep() {
      if (REDUCED() || !('IntersectionObserver' in window)) return;

      this.set(95);
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.classList.add('image-comparison--sweeping');
            this.set(50);
            observer.disconnect();
            // The transition is only for the sweep. Leaving it on would make
            // every later drag lag 800ms behind the pointer.
            setTimeout(() => this.classList.remove('image-comparison--sweeping'), 900);
          });
        },
        { threshold: 0.35 },
      );
      observer.observe(this);
    }
  }

  customElements.define('image-comparison', ImageComparison);
})();
